import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'
import { addDays, dayLabelShort, isSameDay, startOfWeek } from '../../lib/datetime'
import { CalendarEventTypeColor, type CalendarEventDto } from '@jr2os/shared'
import { currentLocaleTag } from '../../lib/formatting'

const START_HOUR = 8
const END_HOUR = 21
const HOUR_HEIGHT_PX = 56

interface Props {
  cursor: Date
  events: CalendarEventDto[]
  onPickSlot: (date: Date) => void
  onPickEvent: (event: CalendarEventDto) => void
}

export default function WeekView({ cursor, events, onPickSlot, onPickEvent }: Props) {
  const { t } = useTranslation('agenda')
  const monday = startOfWeek(cursor)
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i))
  const hours = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i)

  const today = new Date()

  // Los eventos de día completo (hitos de pago, etc.) van en su propia fila:
  // en la rejilla horaria se apilaban en el mismo hueco y quedaban ocultos.
  const allDayEvents = events.filter((e) => e.allDay)
  const timedEvents = events.filter((e) => !e.allDay)
  const hasAllDay = allDayEvents.length > 0

  return (
    <div className="surface overflow-hidden">
      {/* Header de días */}
      <div className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))] border-b border-base">
        <div className="px-2 py-2 text-[10px] uppercase tracking-wider t-faint">{t('weekView.hour')}</div>
        {days.map((d) => {
          const todayCol = isSameDay(d, today)
          return (
            <div
              key={d.toISOString()}
              className={[
                'px-2 py-2 text-center border-l border-base/40',
                todayCol ? 'bg-jr2-500/10' : '',
              ].join(' ')}
            >
              <div className="text-[10px] uppercase tracking-wider t-faint">{dayLabelShort(d)}</div>
              <div
                className={['text-sm font-semibold', todayCol ? 'text-jr2-700 dark:text-jr2-300' : 't-primary'].join(
                  ' '
                )}
              >
                {d.getDate()}
              </div>
            </div>
          )
        })}
      </div>

      {/* Fila "todo el día": chips apilados por día (hitos de pago, jornadas…) */}
      {hasAllDay && (
        <div className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))] border-b border-base bg-[var(--bg-surface-2)]">
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider t-faint">
            {t('weekView.allDay')}
          </div>
          {days.map((d) => {
            const dayEvents = allDayEvents.filter((e) => eventOverlapsDay(e, d))
            return (
              <div key={d.toISOString()} className="border-l border-base/40 px-1 py-1 space-y-1 min-w-0">
                {dayEvents.map((e) => {
                  const color = CalendarEventTypeColor[e.type]
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => onPickEvent(e)}
                      className={[
                        'block w-full px-1.5 py-0.5 rounded text-left text-[11px] border truncate',
                        color.bg,
                        color.text,
                        color.border,
                        'hover:brightness-125 transition',
                      ].join(' ')}
                      title={`${t('enums:CalendarEventType.' + e.type)} · ${e.title}`}
                    >
                      {e.title}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}

      <div
        className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))] relative"
        style={{ height: HOUR_HEIGHT_PX * hours.length }}
      >
        {/* Columna de horas */}
        <div className="relative">
          {hours.map((h) => (
            <div
              key={h}
              className="text-[10px] t-faint pr-2 text-right border-t border-base/30"
              style={{ height: HOUR_HEIGHT_PX }}
            >
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {days.map((d) => (
          <DayColumn
            key={d.toISOString()}
            day={d}
            hours={hours}
            events={timedEvents.filter((e) => eventOverlapsDay(e, d))}
            onPickSlot={(hour) => {
              const slot = new Date(d)
              slot.setHours(hour, 0, 0, 0)
              onPickSlot(slot)
            }}
            onPickEvent={onPickEvent}
          />
        ))}
      </div>
    </div>
  )
}

function eventOverlapsDay(e: CalendarEventDto, d: Date): boolean {
  const start = new Date(e.startAt)
  const end = e.endAt ? new Date(e.endAt) : new Date(start.getTime() + 60 * 60 * 1000)
  const dayStart = new Date(d)
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(d)
  dayEnd.setHours(23, 59, 59, 999)
  return start < dayEnd && end > dayStart
}

function DayColumn({
  day,
  hours,
  events,
  onPickSlot,
  onPickEvent,
}: {
  day: Date
  hours: number[]
  events: CalendarEventDto[]
  onPickSlot: (hour: number) => void
  onPickEvent: (event: CalendarEventDto) => void
}) {
  const positioned = layoutTimedEvents(events, day, hours)
  return (
    <div className="relative border-l border-base/40">
      {/* Slots por hora */}
      {hours.map((h) => (
        <button
          key={h}
          type="button"
          onClick={() => onPickSlot(h)}
          className="block w-full hover:bg-jr2-500/5 border-t border-base/30"
          style={{ height: HOUR_HEIGHT_PX }}
          aria-label={i18n.t('agenda:weekView.createEventAt', { hour: h })}
        />
      ))}
      {/* Eventos absolutos — los solapados se reparten en columnas */}
      {positioned.map(({ e, top, height, col, cols }) => {
        const color = CalendarEventTypeColor[e.type]
        return (
          <button
            key={e.id}
            type="button"
            onClick={() => onPickEvent(e)}
            className={[
              'absolute px-1.5 py-1 rounded text-left text-[11px] border',
              color.bg,
              color.text,
              color.border,
              'hover:brightness-125 transition overflow-hidden',
            ].join(' ')}
            style={{
              top,
              height,
              left: `calc(${(col / cols) * 100}% + 2px)`,
              width: `calc(${100 / cols}% - 4px)`,
            }}
            title={`${i18n.t('enums:CalendarEventType.' + e.type)} · ${e.title}`}
          >
            <div className="font-semibold truncate">{e.title}</div>
            {height > 28 && cols <= 2 && (
              <div className="text-[10px] opacity-80 truncate">{formatRange(e)}</div>
            )}
            {e.opportunityCode && height > 42 && cols <= 2 && (
              <div className="text-[10px] opacity-70 truncate font-mono">{e.opportunityCode}</div>
            )}
          </button>
        )
      })}
    </div>
  )
}

interface PositionedEvent {
  e: CalendarEventDto
  top: number
  height: number
  col: number
  cols: number
}

/**
 * Coloca los eventos con hora repartiendo en columnas los que se solapan:
 * se agrupan en "clusters" de solape transitivo y dentro de cada cluster cada
 * evento ocupa la primera columna libre; el ancho se divide entre columnas.
 */
function layoutTimedEvents(
  events: CalendarEventDto[],
  day: Date,
  hours: number[]
): PositionedEvent[] {
  const items = events
    .map((e) => ({ e, p: placeEvent(e, day, hours) }))
    .filter((x): x is { e: CalendarEventDto; p: { top: number; height: number } } => x.p !== null)
    .sort((a, b) => a.p.top - b.p.top || b.p.height - a.p.height)

  const out: PositionedEvent[] = []
  let cluster: PositionedEvent[] = []
  let colEnds: number[] = []
  let clusterMaxEnd = -1

  const flush = () => {
    const n = Math.max(1, colEnds.length)
    for (const it of cluster) it.cols = n
    out.push(...cluster)
    cluster = []
    colEnds = []
    clusterMaxEnd = -1
  }

  for (const { e, p } of items) {
    if (cluster.length > 0 && p.top >= clusterMaxEnd) flush()
    let col = colEnds.findIndex((end) => end <= p.top)
    if (col === -1) {
      col = colEnds.length
      colEnds.push(0)
    }
    colEnds[col] = p.top + p.height
    clusterMaxEnd = Math.max(clusterMaxEnd, p.top + p.height)
    cluster.push({ e, top: p.top, height: p.height, col, cols: 1 })
  }
  flush()
  return out
}

function placeEvent(
  e: CalendarEventDto,
  day: Date,
  hours: number[]
): { top: number; height: number } | null {
  const startHour = hours[0]!
  const lastHour = hours[hours.length - 1]!
  const dayStart = new Date(day)
  dayStart.setHours(startHour, 0, 0, 0)
  const dayEnd = new Date(day)
  dayEnd.setHours(lastHour + 1, 0, 0, 0)

  const eStart = new Date(e.startAt)
  const eEnd = e.endAt ? new Date(e.endAt) : new Date(eStart.getTime() + 60 * 60 * 1000)

  const start = eStart < dayStart ? dayStart : eStart
  const end = eEnd > dayEnd ? dayEnd : eEnd
  if (end <= dayStart || start >= dayEnd) return null

  const top = ((start.getHours() - startHour) * 60 + start.getMinutes()) * (HOUR_HEIGHT_PX / 60)
  const heightMin = (end.getTime() - start.getTime()) / 60000
  return { top, height: Math.max(20, heightMin * (HOUR_HEIGHT_PX / 60)) }
}

function formatRange(e: CalendarEventDto): string {
  if (e.allDay) return i18n.t('agenda:weekView.allDay')
  const start = new Date(e.startAt).toLocaleTimeString(currentLocaleTag(), {
    hour: '2-digit',
    minute: '2-digit',
  })
  if (!e.endAt) return start
  const end = new Date(e.endAt).toLocaleTimeString(currentLocaleTag(), { hour: '2-digit', minute: '2-digit' })
  return `${start}–${end}`
}
